import { CurrencyHourly, CurrencyMonthly } from "@/components/l10n/Currency.tsx";
import { LoadingSuspense } from "@/components/loading/Loading.tsx";
import { WModal, WModalContent } from "@/components/modal/WModal.tsx";
import { WTable, WTableBody } from "@/components/table/WTable.tsx";
import { eBillingAccountQueryAtom } from "@/modules/billing/billingAccountQuery.ts";
import type { EBillingAccount } from "@/modules/billing/billingLogic.tsx";
import { LocationSelectCustom } from "@/modules/location/LocationSelect.tsx";
import { PoolSelectCustom } from "@/modules/location/PoolSelect.tsx";
import { getPreferredLocation, type LocationWithEnabled } from "@/modules/location/location.ts";
import type { LocationSlug } from "@/modules/location/locationQuery.ts";
import { usePricingLocations } from "@/modules/location/locationQuery.ts";
import { hostPoolQueryAtom, type HostPoolUUID, type HostPoolWithType } from "@/modules/pools/hostPoolsQuery.ts";
import { getDefaultHostPool } from "@/modules/pools/logic.ts";
import { pricesAtom } from "@/modules/pricing/pricingQuery.ts";
import { getHostPoolKey, getLocationPrices, type PricePoolKey } from "@/modules/pricing/resourcePricing.ts";
import { useSuspenseQueryAtom } from "@/utils/query/useSuspenseQueryAtom.ts";
import { Trans, useLingui } from "@lingui/react/macro";
import type { PriceFieldsBase } from "@warrenio/api-spec/spec.oats.gen";
import { type PropsWithChildren, type ReactNode, useRef, useState } from "react";
import { type AriaButtonOptions, useButton } from "react-aria";
import { Label } from "react-aria-components";

function ShowPricingModalButton({ children, ...props }: PropsWithChildren<AriaButtonOptions<"span">>) {
    const ref = useRef(null);
    // Can not use `WButton` because this is an inline element (span)
    const { buttonProps } = useButton(
        {
            ...props,
            elementType: "span",
        },
        ref,
    );

    return (
        <span className="color-primary cursor-pointer" ref={ref} {...buttonProps}>
            {children}
        </span>
    );
}

export function PricingTable({ slug, poolKey }: { slug: LocationSlug; poolKey: PricePoolKey | undefined }) {
    const pricing = useSuspenseQueryAtom(pricesAtom);

    const locationPrices = getLocationPrices(pricing, slug, poolKey);
    const priceRows: PriceFieldsBase[] = Object.entries(locationPrices).flatMap(([_resourceType, value]) => value);

    return (
        <WTable>
            <thead>
                <tr>
                    <th />
                    <th className="text-right">
                        <Trans>Price per hour</Trans>
                    </th>
                    <th className="text-right">
                        <Trans>Price per month</Trans>
                    </th>
                </tr>
            </thead>
            <WTableBody>
                {priceRows.map((item, index) => (
                    <tr key={index}>
                        <td className="color-primary">{item.description}</td>
                        <td className="text-right">
                            <CurrencyHourly value={item.price_per_unit} />
                        </td>
                        <td className="text-right">
                            <CurrencyMonthly value={item.price_per_unit} accurate={true} />
                        </td>
                    </tr>
                ))}
            </WTableBody>
        </WTable>
    );
}

export interface PricingModalProps {
    children: ReactNode;
}

export function PricingModal({ children, ...props }: PricingModalProps & PricingModalContentProps) {
    return (
        <WModal button={<ShowPricingModalButton>{children}</ShowPricingModalButton>}>
            <PricingModalContent {...props} />
        </WModal>
    );
}

interface PricingModalContentProps extends PricingModalDataProps {
    billingAccountId?: number;
}

function DiscountNotice({ account }: { account: EBillingAccount }) {
    const { title, discount_percentage } = account.account;

    return (
        <p className="pt-3 color-muted font-size-small">
            <Trans comment="Price list annotation">
                Billing account "{title}" has discount {discount_percentage}%
            </Trans>
        </p>
    );
}

export function PricingModalContent({ billingAccountId, ...props }: PricingModalContentProps) {
    const { t } = useLingui();

    const data = useSuspenseQueryAtom(eBillingAccountQueryAtom);
    const billingAccount = billingAccountId ? data.get(billingAccountId) : undefined;

    return (
        <WModalContent title={t`Price list`} modalAction={undefined}>
            <p className="pb-4">
                <Trans>
                    The actual cost of your resource is calculated based on the below prices and your actual hourly
                    usage. All prices are per one unit of resource (1 vCPU or 1 GiB). Prices do not contain taxes.
                </Trans>
            </p>
            <p className="font-size-subtitle pb-3">
                <Trans>Prices may vary for the options below</Trans>
            </p>

            <LoadingSuspense>
                <PricingModalData {...props} />
            </LoadingSuspense>

            {billingAccount && <DiscountNotice account={billingAccount} />}
        </WModalContent>
    );
}

export interface PricingModalDataProps {
    defaultLocation?: LocationSlug;
    defaultHostPool?: HostPoolUUID | null;
}

function PricingModalData({ defaultLocation, defaultHostPool }: PricingModalDataProps) {
    //#region Hooks

    const locations = usePricingLocations();

    const [selectedLocation, setSelectedLocation] = useState<LocationWithEnabled>();
    const [selectedPool, setSelectedPool] = useState<HostPoolWithType>();

    const location = selectedLocation ?? getPreferredLocation(locations, defaultLocation);
    const poolsMap = useSuspenseQueryAtom(hostPoolQueryAtom(location.slug));

    //#endregion Hooks

    const locationPools = [...poolsMap.values()].filter((l) => l.is_visible);
    const pool = selectedPool ?? getDefaultHostPool(locationPools, defaultHostPool);

    function onChangeLocation(location: LocationWithEnabled) {
        setSelectedPool(undefined);
        setSelectedLocation(location);
    }

    // TODO: should locations always be shown in some cases?
    const hasLocations = locations.length > 1;
    // TODO: should host pool be shown when there is only one?
    const hasPools = locationPools.length > 1;
    const hasSelects = hasLocations || hasPools;

    return (
        <>
            {hasSelects && (
                <div className="flex gap-1rem pb-4 justify-between">
                    {hasLocations && (
                        <div className="flex-1">
                            <Label className="block pb-1">
                                <Trans>Location</Trans>:
                            </Label>
                            <LocationSelectCustom items={locations} value={location} onChange={onChangeLocation} />
                        </div>
                    )}

                    {/* Always leave space for the pool selector because it can (dis)appear when switching between locations */}
                    <div className="flex-1">
                        {hasPools && (
                            <>
                                <Label className="block pb-1">
                                    <Trans>Server class</Trans>:
                                </Label>
                                <PoolSelectCustom
                                    items={locationPools}
                                    location={location.slug}
                                    value={pool}
                                    onChange={setSelectedPool}
                                />
                            </>
                        )}
                    </div>
                </div>
            )}

            <PricingTable slug={location.slug} poolKey={pool ? getHostPoolKey(pool.uuid) : undefined} />
        </>
    );
}
